import React, { useState, useEffect } from 'react';
import { MdClose, MdInfo, MdOutlineCookie } from 'react-icons/md';
import { NavLink } from 'react-router-dom';
import Button from '../Button/Button';
import styles from './CookieConsent.module.css';
import { getTranslation } from '../../utils/translations';
import { updateConsentMode, deleteGoogleAnalyticsCookies } from '../../utils/googleAnalytics';

interface CookieSettings {
    necessary: boolean;
    functional: boolean;
    analytics: boolean;
    performance: boolean;
}

interface TooltipInfo {
    necessary: string;
    functional: string;
    analytics: string;
    performance: string;
}

const tooltipInfo: TooltipInfo = {
    necessary: "cookieConsent.tooltip.necessary",
    functional: "cookieConsent.tooltip.functional",
    analytics: "cookieConsent.tooltip.analytics",
    performance: "cookieConsent.tooltip.performance"
};

// Cookie names for each type of consent
const COOKIE_NAMES = {
    CONSENT_GIVEN: 'appotype_consent_given',
    NECESSARY: 'appotype_cookie_necessary',
    FUNCTIONAL: 'appotype_cookie_functional',
    ANALYTICS: 'appotype_cookie_analytics',
    PERFORMANCE: 'appotype_cookie_performance'
};

// Cookie expiration in days
const COOKIE_EXPIRATION_DAYS = 365;

const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
};

const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop()?.split(';').shift() || null;
    }
    return null;
};

const deleteCookie = (name: string) => {
    // Delete cookie with multiple combinations to ensure it's removed
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Strict`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    
    // Also try with max-age for better browser compatibility
    document.cookie = `${name}=;max-age=0;path=/;SameSite=Strict`;
    document.cookie = `${name}=;max-age=0;path=/`;
};

export const CookieConsent = () => {
    const [isVisible, setIsVisible] = useState(true);
    const [showSettings, setShowSettings] = useState(false);
    const [activeTooltip, setActiveTooltip] = useState<keyof CookieSettings | null>(null);
    const [settings, setSettings] = useState<CookieSettings>({
        necessary: true,
        functional: true,
        analytics: true,
        performance: true
    });
    // Use language from localStorage or default to 'pl'
    const language = localStorage.getItem('language') as 'pl' | 'en' || 'pl';

    useEffect(() => {
        // Check if consent has been given before
        const consentGiven = getCookie(COOKIE_NAMES.CONSENT_GIVEN);
        
        // Always try to load saved settings from cookies
        const functionalCookie = getCookie(COOKIE_NAMES.FUNCTIONAL);
        const analyticsCookie = getCookie(COOKIE_NAMES.ANALYTICS);
        const performanceCookie = getCookie(COOKIE_NAMES.PERFORMANCE);
        const necessaryCookie = getCookie(COOKIE_NAMES.NECESSARY);
        
        // Check if we have any existing preferences (including necessary cookie which is always set)
        const hasExistingPreferences = necessaryCookie !== null;
        
        if (hasExistingPreferences) {
            // Load saved settings from cookies
            // If a cookie doesn't exist, it means it was set to false and then deleted
            const savedSettings: CookieSettings = {
                necessary: true, // Always true
                functional: functionalCookie === 'true',
                analytics: analyticsCookie === 'true',
                performance: performanceCookie === 'true'
            };
            
            setSettings(savedSettings);
            
            // Update consent mode with current analytics setting
            updateConsentMode(savedSettings.analytics);
            
            // If user has existing preferences but consent was deleted (e.g., from CookiesPage),
            // show the settings view directly since they're managing their preferences
            if (!consentGiven) {
                setShowSettings(true);
            }
        }
        
        // Hide the component only if consent was previously given
        if (consentGiven) {
            setIsVisible(false);
        }
    }, []);

    const saveSettings = (newSettings: CookieSettings) => {
        // Set consent and necessary cookies (always true)
        setCookie(COOKIE_NAMES.CONSENT_GIVEN, 'true', COOKIE_EXPIRATION_DAYS);
        setCookie(COOKIE_NAMES.NECESSARY, 'true', COOKIE_EXPIRATION_DAYS);
        
        // Handle optional cookies: either set to 'true' or delete entirely
        if (newSettings.functional) {
            setCookie(COOKIE_NAMES.FUNCTIONAL, 'true', COOKIE_EXPIRATION_DAYS);
        } else {
            deleteCookie(COOKIE_NAMES.FUNCTIONAL);
        }
        
        if (newSettings.analytics) {
            setCookie(COOKIE_NAMES.ANALYTICS, 'true', COOKIE_EXPIRATION_DAYS);
            // Update Google Consent Mode to granted
            updateConsentMode(true);
        } else {
            deleteCookie(COOKIE_NAMES.ANALYTICS);
            // Update Google Consent Mode to denied
            updateConsentMode(false);
            // Aggressively delete Google Analytics cookies when consent is withdrawn
            deleteGoogleAnalyticsCookies();
            // Clean up again after a short delay to catch any lingering cookies
            setTimeout(() => {
                deleteGoogleAnalyticsCookies();
            }, 100);
        }
        
        if (newSettings.performance) {
            setCookie(COOKIE_NAMES.PERFORMANCE, 'true', COOKIE_EXPIRATION_DAYS);
        } else {
            deleteCookie(COOKIE_NAMES.PERFORMANCE);
        }

        setSettings(newSettings);
        setIsVisible(false);
        
        // Dispatch custom event to notify analytics hook of consent change
        const event = new CustomEvent('cookieConsentChange', {
            detail: { analytics: newSettings.analytics }
        });
        window.dispatchEvent(event);
        
        // Refresh the page after a short delay to ensure cookie changes are visible
        setTimeout(() => {
            window.location.reload();
        }, 100);
    };

    const handleToggle = (key: keyof CookieSettings) => {
        if (key === 'necessary') return;
        setSettings(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleAcceptAll = () => {
        const allAccepted: CookieSettings = {
            ...settings,
            functional: true,
            analytics: true,
            performance: true
        };
        saveSettings(allAccepted);
    };

    const handleAcceptSelected = () => {
        saveSettings(settings);
    };

    const handleClose = () => {
        // When closing without making a choice, save current settings
        saveSettings(settings);
    };

    const handleInfoClick = (key: keyof CookieSettings, e: React.MouseEvent) => {
        e.stopPropagation();
        setActiveTooltip(activeTooltip === key ? null : key);
    };

    if (!isVisible) return null;

    const renderMainView = () => (
        <div className={styles.header}>
            <MdOutlineCookie size={44} className={styles.cookieIcon} />
            <div className={styles.content}>
                <div className={styles.titleContainer}>
                    <h2 className={styles.title}>{getTranslation('cookieConsent.title', language)}</h2>
                    <button onClick={handleClose} className={styles.closeButton}>
                        <MdClose size={24} />
                    </button>
                </div>
                <p className={styles.description}>
                    {getTranslation('cookieConsent.description', language)}{' '}
                    <NavLink to="/privacy" className={styles.link}>
                        {getTranslation('cookieConsent.privacyLink', language)}
                    </NavLink>
                </p>
                <div className={styles.buttonContainer}>
                    <Button variant="outline" onClick={() => setShowSettings(true)} className={styles.outlineButton}>
                        {getTranslation('cookieConsent.customizeButton', language)}
                    </Button>
                    <Button variant="secondary" onClick={handleAcceptAll} className={styles.acceptButton}>
                        {getTranslation('cookieConsent.acceptButton', language)}
                    </Button>
                </div>
            </div>
        </div>
    );

    const renderSettingsView = () => (
        <div className={styles.header}>
            <MdOutlineCookie size={44} className={styles.cookieIcon} />
            <div className={styles.content}>
                <div className={styles.titleContainer}>
                    <h2 className={styles.title}>{getTranslation('cookieConsent.title', language)}</h2>
                    <button onClick={handleClose} className={styles.closeButton}>
                        <MdClose size={24} />
                    </button>
                </div>
                <p className={styles.description}>
                    {getTranslation('cookieConsent.description', language)}{' '}
                    <NavLink to="/privacy" className={styles.link}>
                        {getTranslation('cookieConsent.privacyLink', language)}
                    </NavLink>
                </p>
                <div className={styles.cookieOptions}>
                    {(Object.keys(settings) as Array<keyof CookieSettings>).map(key => (
                        <div key={key} className={styles.cookieOption}>
                            <div className={styles.optionHeader}>
                                <span>{getTranslation(`cookieConsent.${key}`, language)}</span>
                                <div className={styles.tooltipContainer}>
                                    <button
                                        className={styles.infoButton}
                                        onClick={(e) => handleInfoClick(key, e)}
                                    >
                                        <MdInfo size={20} />
                                    </button>
                                    {activeTooltip === key && (
                                        <div className={styles.tooltip}>
                                            {getTranslation(tooltipInfo[key], language)}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <label className={styles.switch}>
                                <input
                                    type="checkbox"
                                    checked={settings[key]}
                                    disabled={key === 'necessary'}
                                    onChange={() => handleToggle(key)}
                                />
                                <span className={styles.slider} />
                            </label>
                        </div>
                    ))}
                </div>
                <div className={styles.buttonContainer}>
                    <Button variant="outline" onClick={handleAcceptAll} className={styles.outlineButton}>
                        {getTranslation('cookieConsent.acceptAllButton', language)}
                    </Button>
                    <Button variant="secondary" onClick={handleAcceptSelected} className={styles.acceptButton}>
                        {getTranslation('cookieConsent.acceptSelectedButton', language)}
                    </Button>
                </div>
            </div>
        </div>
    );

    return (
        <div className={styles.container}>
            {showSettings ? renderSettingsView() : renderMainView()}
        </div>
    );
}

export default CookieConsent; 